import { Dropdown, Popconfirm, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { HoverTooltip } from "../../BaseComps/InfoTooltip";
import { shortenTextAEStyle } from "../../helpers/text";
import { useMst } from "../../stores/main";
import { isInteractiveMod } from "../../helpers/modTypeGuards";
import { modTypes } from "@blings/blings-player";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import {
  ConnectorIcon,
  DeleteIcon,
  DuplicateIcon,
  EditPenIcon,
  EyeIcon,
  EyeIconInvisible,
  ThreeDotsDraggable,
  ThreeDotsHorizontal,
} from "../../icons-and-animations/Icons";
import type { FormTitle2Result } from "./mod-definitions";

// Subtitle component for displaying the value/expression text only
function SubtitleContent({
  formTitleText2,
  isActiveMod,
}: {
  formTitleText2: FormTitle2Result | undefined;
  isActiveMod: boolean;
}) {
  if (!formTitleText2 || !formTitleText2.text) return null;

  const textContent =
    typeof formTitleText2.text === "string"
      ? shortenTextAEStyle(formTitleText2.text, 30)
      : formTitleText2.text;

  const titleText =
    typeof formTitleText2.text === "string" ? formTitleText2.text : undefined;

  return (
    <div
      className={`subtitle-content ${isActiveMod ? "isActive" : ""}`}
      title={titleText}
    >
      <span className="subtitle-content__text">{textContent}</span>
    </div>
  );
}

const CONNECTOR_ERROR_COPY: Record<string, string> = {
  "Invalid data key":
    "Data key not found in the current per-video data. Pick an existing data key or update the incoming data.",
  "Invalid live control key":
    "Live control key not found in the current live control data. Pick an existing live control key or update the live control payload.",
  "Invalid input name":
    "Input name is not registered in this project. Select an existing input or create it first.",
  "Value is not valid":
    "No valid value was found for this connector. Add a value, data key, live control key, input name, or expression.",
  "Asset URL is not valid":
    "Asset URL is unreachable or is not an image, video, or audio file.",
  "Asset URL is empty": "Asset URL is empty. Add a media URL before saving.",
  "Error in expression":
    "Expression failed to run. Check the JavaScript expression and returned value.",
  "Color code is not valid":
    "Color value must be a valid hex code, such as #FF00FF.",
  "No scenes or flows selected": "No scenes or flows are selected.",
  "No inputs selected": "No inputs are selected.",
  "Missing name": "Input connector is missing a name.",
  changeSceneDurationToMediaDuration:
    "Only one asset connector can change the scene duration to its media duration. Disable this setting on the extra asset connector.",
};

type ConnectorErrorSection = {
  title: string;
  messages: string[];
};

const formatConnectorError = (error: string) =>
  CONNECTOR_ERROR_COPY[error] || error;

const createErrorSection = (
  title: string,
  messages: string[]
): ConnectorErrorSection | null => {
  const formattedMessages = messages
    .filter(Boolean)
    .map((message) => formatConnectorError(message));

  if (!formattedMessages.length) return null;
  return { title, messages: formattedMessages };
};

const ConnectorTooltipContent = ({
  connectorTitle,
  sections,
}: {
  connectorTitle: string;
  sections: ConnectorErrorSection[];
}) => (
  <div className="connector-status-tooltip">
    <div className="connector-status-tooltip__title">{connectorTitle}</div>
    {sections.length > 0 && (
      <div className="connector-status-tooltip__sections">
        {sections.map((section) => (
          <div
            className="connector-status-tooltip__section"
            key={section.title}
          >
            <div className="connector-status-tooltip__section-title">
              {section.title}
            </div>
            {section.messages.map((message, index) => (
              <div
                className="connector-status-tooltip__message"
                key={`${message}-${index}`}
              >
                {message}
              </div>
            ))}
          </div>
        ))}
      </div>
    )}
  </div>
);

export function PanelHeader({
  type,
  handleRemove,
  formTitleText,
  formTitleText2,
  handleCopy,
  modTitle,
  modName,
  hasMultiConnectedLayer,
  hasDataErrors,
  dataErrors,
  hasBrokenLayers,
  brokenLayerErrors,
  hasMissingProperties,
  missingPropertyErrors,
  modId,
  hasMultipleDynamicLength,
  multipleDynamicLengthErrors,
  isDisabled,
  setConnectorDisabledState,
  setActiveMod,
  isActiveMod,
  onChange,
  listeners,
}: {
  handleRemove;
  type: modTypes;
  onChange: (i: any, k: any, v: any) => void;
  handleCopy;
  formTitleText: string;
  formTitleText2?: FormTitle2Result | undefined;
  modTitle: string;
  modName: string | null;
  hasMultiConnectedLayer: boolean;
  hasDataErrors: boolean;
  dataErrors: string[];
  hasBrokenLayers: boolean;
  brokenLayerErrors: string[];
  hasMissingProperties: boolean;
  missingPropertyErrors: string[];
  modId: string;
  hasMultipleDynamicLength: boolean;
  multipleDynamicLengthErrors: string[];
  isDisabled: boolean;
  setConnectorDisabledState: (state: boolean) => void;
  setActiveMod: (modId: string | number | undefined) => void;
  isActiveMod: boolean;
  listeners?: SyntheticListenerMap | undefined;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [clickTimeoutId, setClickTimeoutId] = useState<any>(null);
  const currentModName = modName || "";
  const { modsStore } = useMst();

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      changeModName(modId, event.target.value);
      setIsEditing(false);
    }
  };

  useEffect(() => {
    return () => {
      if (clickTimeoutId) clearTimeout(clickTimeoutId);
    };
  }, [clickTimeoutId]);

  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (clickTimeoutId !== null) {
      clearTimeout(clickTimeoutId);
      setClickTimeoutId(null);
      handleDoubleClick(event);
    } else {
      const timeoutId = setTimeout(() => {
        setClickTimeoutId(null);
        if (isActiveMod) setActiveMod(undefined);
        else setActiveMod(modId);
      }, 200);
      setClickTimeoutId(timeoutId);
    }
  };

  const handleDoubleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setIsEditing(true);
  };

  const handleBlur = (e) => {
    setIsEditing(false);
    if (e.target.value === modName) return;
    if (e.target.value === "") {
      changeModName(modId, isInteractiveMod(type) ? "" : undefined);
      return;
    }
    changeModName(modId, e.target.value);
  };

  const changeModName = (modId, modName) => {
    if (isInteractiveMod(type)) {
      onChange(modId, ["ctaName"], [modName]);
    } else {
      modsStore.updateModName(modId, modName);
    }
  };

  const hasErrors =
    hasMultiConnectedLayer ||
    hasDataErrors ||
    hasBrokenLayers ||
    hasMultipleDynamicLength ||
    hasMissingProperties;
  const tooltipSections = [
    hasMultiConnectedLayer
      ? createErrorSection("Duplicated layer", [
          "This layer is already controlled by another connector of the same type. Change the target layer or disable one connector.",
        ])
      : null,
    createErrorSection("Data", dataErrors),
    createErrorSection("Broken layer", brokenLayerErrors),
    createErrorSection("Dynamic length", multipleDynamicLengthErrors),
    createErrorSection("Missing properties", missingPropertyErrors),
  ].filter((section): section is ConnectorErrorSection => Boolean(section));
  const connectorTooltipTitle = `${modTitle} connector`;
  const divider = (
    <div className="connection-type-divider">
      <svg
        width="1"
        height="17"
        viewBox="0 0 1 17"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="0.75"
          y="0.25"
          width="16.5"
          height="0.5"
          transform="rotate(90 0.75 0.25)"
          stroke="#424242"
          strokeWidth="0.5"
        />
      </svg>
    </div>
  );

  const TitleTexts = () => {
    if (formTitleText2) {
      const layerNamePlaceholder = formTitleText;
      const titleTextClassName =
        "modPanelText" + (isActiveMod ? " isActive" : "");

      return (
        <>
          {isEditing ? (
            <div className="title-edit-container">
              <input
                type="text"
                defaultValue={currentModName}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                autoFocus
                className={"modEditConnectorName"}
                placeholder={layerNamePlaceholder}
                title={currentModName || layerNamePlaceholder}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                }}
              />
            </div>
          ) : modName ? (
            <div className="title-display pencilEditName">
              <div className="title-text-wrap">
                <span className={titleTextClassName} title={currentModName}>
                  {currentModName}
                </span>
              </div>
              <div
                onClick={(e) => handleDoubleClick(e)}
                className={`modIcon editNameIcon ${
                  isHovered ? "is-visible" : ""
                }`}
              >
                <EditPenIcon size={12} />
              </div>
            </div>
          ) : (
            <div className="title-display">
              <div
                className={`${titleTextClassName} mr5`}
                title={formTitleText}
              >
                {shortenTextAEStyle(formTitleText, 20)}
              </div>
              <div className="pencilEditName">
                <div
                  onClick={(e) => handleDoubleClick(e)}
                  className={`modIcon editNameIcon ${
                    isHovered ? "is-visible" : ""
                  }`}
                >
                  <EditPenIcon />
                </div>
              </div>
            </div>
          )}
        </>
      );
    }

    return (
      <div className="title-display">
        <div className="modPanelText mr5" title={formTitleText}>
          {shortenTextAEStyle(formTitleText, 20)}
        </div>
      </div>
    );
  };

  const dropdownMenuItems = {
    items: [
      {
        key: "1",
        label: (
          <HoverTooltip
            title={isDisabled ? "Enable Connector" : "Disable Connector"}
            description={
              isDisabled
                ? "Turn this connector back on."
                : "Temporarily stop this connector from changing the layer."
            }
            immediate
            placement="right"
          >
            <div
              className={"modPanelBtn modPanelBtn_disable"}
              onClick={(event) => {
                event.stopPropagation();
                setConnectorDisabledState(!isDisabled);
              }}
            >
              {isDisabled ? <EyeIconInvisible /> : <EyeIcon />}
            </div>
          </HoverTooltip>
        ),
      },
      {
        key: "2",
        label: (
          <Tooltip title="Duplicate connector" placement="right">
            <div
              className={"modPanelBtn modPanelBtn_copy"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCopy();
              }}
            >
              <DuplicateIcon />
            </div>
          </Tooltip>
        ),
      },
      {
        key: "3",
        label: (
          <Popconfirm
            title='Delete Connector? (nothing is saved till you press "Save Connectors")'
            placement="right"
            onConfirm={(event) => {
              event?.stopPropagation();
              event?.preventDefault();
              handleRemove();
            }}
            onCancel={(event) => {
              event?.stopPropagation();
              event?.preventDefault();
            }}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete connector" placement="right">
              <div
                className={"modPanelBtn modPanelBtn_remove"}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                }}
              >
                <DeleteIcon />
              </div>
            </Tooltip>
          </Popconfirm>
        ),
      },
    ],
  };

  // ant-dropdown-menu-item ant-dropdown-menu-item-only-child

  return (
    <div
      className={"ModsListPanelHeader"}
      onClick={handleClick}
      onMouseEnter={() => {
        !isEditing && setIsHovered(true);
      }}
      onMouseLeave={() => {
        !isEditing && setIsHovered(false);
      }}
    >
      {/* Drag handle */}
      <div
        className={[
          "draggable-superset-icon",
          isActiveMod ? "isActive" : "",
        ].join(" ")}
        {...listeners}
      >
        <ThreeDotsDraggable className="draggable-icon" />
      </div>

      {/* Main content: icon + text + connection-type-icon */}
      <div className="header-content">
        <div className="icon-container">
          <Tooltip
            overlay={
              <ConnectorTooltipContent
                connectorTitle={connectorTooltipTitle}
                sections={tooltipSections}
              />
            }
            placement={"leftTop"}
            styles={{ root: { maxWidth: 340 } }}
          >
            <div
              className={`mod-icon-new ${hasErrors ? "error-state" : ""}`}
              aria-label={connectorTooltipTitle}
            >
              <ConnectorIcon type={type} />
            </div>
          </Tooltip>
        </div>

        <div className={`text-container ${hasErrors ? "error-state" : ""}`}>
          <div className="title-row">
            <TitleTexts />
          </div>
          {formTitleText2 && formTitleText2.text && (
            <div className="subtitle-row">
              <SubtitleContent
                formTitleText2={formTitleText2}
                isActiveMod={isActiveMod}
              />
            </div>
          )}
        </div>
      </div>

      {/* Menu buttons */}
      <div className={"modPanelBtns"}>
        <div className="extra-buttons">
          {/* Connection type icon (data/live control/edit/code) */}
          {formTitleText2?.typeIcon && (
            <>
              <div className="connection-type-icon">
                {formTitleText2.typeIcon}
              </div>
              {divider}
            </>
          )}

          <div>
            <Dropdown
              menu={dropdownMenuItems}
              rootClassName="dropdown-menu-extra-buttons"
            >
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                }}
                style={{
                  border: "none",
                  background: "none",
                  padding: "0",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ThreeDotsHorizontal />
              </button>
            </Dropdown>
          </div>
        </div>
      </div>
    </div>
  );
}
